import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { NG1_UIB_MODAL } from '@commons/core/core.provider.ajs';
import { ITimelineEntry, ITimelineInprocessEntry, TimelineEntry } from '@commons/history/timeline/models/timeline-entry';
import { ContactEntryVisibilityType } from '@commons/models/contact-entry';
import { isDefined } from 'angular';

import { StorageService } from '@contact/upgrade';

@Component({
  selector: 'cng-timeline-inprocess',
  templateUrl: 'timeline-inprocess.component.html',
  styleUrls: ['./timeline-inprocess.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimelineInprocessComponent implements OnChanges {
  @Input() timelines: ITimelineEntry[];
  @Input() responseId: string;
  @Input() currentInteractionId: string;
  @Input() selectInteractionId: string;
  @Output() elementSelectedFn: EventEmitter<ITimelineEntry> = new EventEmitter();
  @Output() elementLinkedFn: EventEmitter<ITimelineEntry[]> = new EventEmitter();

  internalTimeline: ITimelineInprocessEntry[] = [];
  canLink: boolean = false;

  constructor(
    @Inject(NG1_UIB_MODAL) private $uibModal: any,
    private readonly storage: StorageService,
    private readonly sessionDeprecatedService: SessionDeprecatedService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['timelines'] && changes['timelines'].previousValue !== changes['timelines'].currentValue) {
      this.mapTimes(changes['timelines'].currentValue);
    }
  }

  // Send event to parent (history modal) for refresh modal content
  select(row: ITimelineEntry): void {
    this.elementSelectedFn.emit(row);
  }

  link() {
    if (!this.hasTimelineToLink()) {
      return;
    }

    this.$uibModal.open({
      component: 'popupValidate',
      keyboard: false,
      ariaLabelledBy: 'modal-header',
      ariaDescribedBy: 'modal-body',
      resolve: {
        confirm: () => () => this.elementLinkedFn.emit(this.getTimelineToLink()),
        title: () => 'agent:contact.workspace.mail.link.title',
        message: () => 'agent:contact.workspace.mail.link.message',
      },
    });
  }

  canSelectToLink(row: ITimelineInprocessEntry): boolean {
    let hasSkillVisibility = row && row.contactEntry ? row.contactEntry.visibilityType : ContactEntryVisibilityType.PRIVATE;
    return (
      hasSkillVisibility === ContactEntryVisibilityType.AUTHORIZED &&
      !!this.currentInteractionId &&
      this.currentInteractionId !== row.id &&
      row.canalName === 'MAIL'
    );
  }

  hasTimelineToLink(): boolean {
    return this.internalTimeline && this.getTimelineToLink().length !== 0;
  }

  getTimelineToLink(): ITimelineInprocessEntry[] {
    return this.internalTimeline.filter(time => time.selected);
  }

  saveTimelineSelected(): void {
    if (this.responseId) {
      this.storage.remove(TimelineEntry.getLinkStorageKey(this.responseId));
      this.storage.store(
        TimelineEntry.getLinkStorageKey(this.responseId),
        this.getTimelineToLink().map(timeline => timeline.id)
      );
    }
  }

  private mapTimes(timelines: ITimelineEntry[]) {
    if (timelines) {
      if (timelines.length !== 0) {
        if (this.responseId) {
          let timelineToLink: string[] = this.storage.get(TimelineEntry.getLinkStorageKey(this.responseId)).getOrElse([]);
          this.internalTimeline = timelines.map((data: ITimelineEntry) =>
            TimelineEntry.toInProcess(
              data,
              this.sessionDeprecatedService.getProfile().language,
              timelineToLink.some(id => id === data.id)
            )
          );
        } else {
          this.internalTimeline = timelines.map((data: ITimelineEntry) =>
            TimelineEntry.toInProcess(data, this.sessionDeprecatedService.getProfile().language)
          );
        }
        this.canLink =
          isDefined(this.elementLinkedFn) &&
          this.internalTimeline &&
          this.internalTimeline.some(timeline => this.canSelectToLink(timeline));
      } else {
        this.internalTimeline = [];
      }
    }
  }

  get internalTimelineSortedByBeginDate() {
    return this.internalTimeline.sort((a: ITimelineInprocessEntry, b: ITimelineInprocessEntry) => {
      let same = a.beginDate.getTime() === b.beginDate.getTime();
      if (same) return 0;

      // Check if the first is greater than second
      if (a.beginDate > b.beginDate) return -1;

      // Check if the first is less than second
      if (a.beginDate < b.beginDate) return 1;

      return 0;
    });
  }
}
